/**
 * create_job Tool - Creates a QA job for human testing
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { CreateTestJobRequest, JobMetadata } from '@runhuman/shared';
import { DEFAULT_TARGET_DURATION_MINUTES } from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';
import { formatTesterPool } from '../utils/format-tester-pool.js';
import { createJobProperties } from './create-job.schema.js';

export const createJobToolDefinition: Tool = {
  name: 'create_job',
  description: `IMPORTANT: This ONLY creates and queues a job. It does NOT perform the test or return results. You MUST follow up with wait.

Creates a QA job that will be performed by a REAL HUMAN tester (not AI). The human will manually test your application, describe findings in natural language, and AI will extract structured data from their response.

Use this when you need human verification of:
- UI/UX functionality that's hard to automate
- Visual issues, accessibility problems
- Complex user flows (login, checkout, forms)
- Cross-browser compatibility
- Real user experience feedback

REQUIRED WORKFLOW (do NOT skip steps):
1. create_job → Returns jobId (job is now QUEUED, not complete!)
2. wait → Checks status, waits, and retrieves results (takes 2-10 min total)
3. If not complete, call wait again with longer wait time

DO NOT treat job creation as completion. You MUST wait for and retrieve results.`,
  inputSchema: {
    type: 'object',
    properties: createJobProperties,
    required: [],
  },
};

export async function handleCreateJob(
  args: CreateTestJobRequest,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    if (!args.projectId && !args.organizationId) {
      return {
        content: [{
          type: 'text',
          text: `projectId or organizationId is required

You must provide at least one of:
- projectId: The project this test belongs to
- organizationId + githubRepos: To auto-create a project

To find your project ID, use list_projects or visit https://runhuman.com/dashboard`,
        }],
        isError: true,
      };
    }

    // Reference tags (#5932): `commitSha` is a first-class request field folded
    // into metadata server-side; `case`/`pr` are inert tracker refs that live in
    // the generic `refs` bag. Merge with any refs the caller passed via metadata.
    const refInput = args as CreateTestJobRequest & { case?: string; pr?: string };
    const refs: Record<string, string> = { ...args.metadata?.refs };
    if (refInput.case) refs.case = refInput.case;
    if (refInput.pr) refs.pr = refInput.pr;

    const metadata: JobMetadata = {
      ...args.metadata,
      source: 'mcp',
      sourceCreatedAt: new Date().toISOString(),
      mcp: {},
      ...(Object.keys(refs).length > 0 ? { refs } : {}),
    };

    const data = await jobClient.createJob({ ...args, metadata });
    const duration = args.targetDurationMinutes || DEFAULT_TARGET_DURATION_MINUTES;
    const testerPoolInfo = formatTesterPool({
      deviceClass: args.deviceClass,
      requiredDevices: args.requiredDevices,
      requiredLanguages: args.requiredLanguages,
      requireSocialVideos: args.requireSocialVideos,
      requireSideload: args.requireSideload,
    });

    return {
      content: [{
        type: 'text',
        text: `Job QUEUED (not complete yet!)

Job ID: ${data.jobId}
${args.url ? `URL: ${args.url}` : ''}
Time limit: ${duration} minutes${testerPoolInfo}
${data.testability ? `Testability: ${data.testability.isTestable ? 'Yes' : `No - ${data.testability.reason}`}` : ''}

The test has been queued for a HUMAN tester. It is NOT complete yet.

REQUIRED NEXT STEPS:
1. Call wait(jobId: "${data.jobId}", timeoutSeconds: 30)
2. If not complete, call wait again with timeoutSeconds: 60
3. Continue with increasing wait times until complete

Typical completion time: 2-10 minutes total.

DO NOT consider this task done until you have retrieved the results!`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number; code?: string; link?: string };

    if (err.status === 402 || err.code === 'INSUFFICIENT_CREDITS' || err.code === 'INSUFFICIENT_BALANCE') {
      const manageLink = 'https://runhuman.com/dashboard/manage-plan?source=mcp&openModal=true';
      return {
        content: [{
          type: 'text',
          text: `Insufficient Credits

The organization doesn't have enough credits to run this test.

IMPORTANT: Tell the user they need to purchase more credits by visiting:
${manageLink}

Do not attempt to retry until the user confirms they have added credits.`,
        }],
        isError: true,
      };
    }

    return {
      content: [{
        type: 'text',
        text: `Failed to create job

Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}

Please check:
- Your PAT is valid (create one at https://runhuman.com/dashboard/settings/tokens)
- Your network connection
- Your organization has sufficient credits`,
      }],
      isError: true,
    };
  }
}
